import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { S25Util } from "../../../util/s25-util";
import { DemographicSlice } from "./se.analytics.service";

export const NONE_SLICE_VALUE = "(None)";
export const OTHER_BUCKET_LABEL = "Other";

/** Half-open range [min, max) stored as comparable numbers (see {@link DemographicBucketKind}). */
export interface DemographicBucketDef {
    label: string;
    min: number | null;
    max: number | null;
}

export type DemographicBucketKind = "numeric" | "date" | "datetime" | "time";

const DEFAULT_SUGGEST_BUCKET_COUNT = 5;
const HIGH_CARDINALITY_SLICE_THRESHOLD = 10;

export function getDemographicBucketKind(atrbDataType?: string): DemographicBucketKind | undefined {
    if (!atrbDataType) {
        return undefined;
    }
    const code = atrbDataType.trim();
    if (code === CustomAttributes.type.Integer || code === CustomAttributes.type.Float) {
        return "numeric";
    }
    if (code === CustomAttributes.type.Date) {
        return "date";
    }
    if (code === CustomAttributes.type.DateTime) {
        return "datetime";
    }
    if (code === CustomAttributes.type.Time) {
        return "time";
    }
    return undefined;
}

export function isBucketizableDemographicType(atrbDataType?: string): boolean {
    return getDemographicBucketKind(atrbDataType) !== undefined;
}

function padTwo(value: number): string {
    return String(value).padStart(2, "0");
}

function tryParseDate(value: string): Date | null {
    try {
        const parsed = S25Util.date.parse(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch {
        return null;
    }
}

function timeToComparableMinutes(date: Date): number {
    return date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
}

export function parseSliceComparableValue(value: string, kind: DemographicBucketKind): number | null {
    if (!value || value === NONE_SLICE_VALUE) {
        return null;
    }

    if (kind === "numeric") {
        const parsed = Number.parseFloat(value.trim());
        return Number.isFinite(parsed) ? parsed : null;
    }

    const parsedDate = tryParseDate(value);
    if (!parsedDate) {
        return null;
    }

    if (kind === "time") {
        return timeToComparableMinutes(parsedDate);
    }

    if (kind === "date") {
        const startOfDay = S25Util.date.toStartOfDay(parsedDate);
        return startOfDay?.getTime() ?? null;
    }

    return parsedDate.getTime();
}

export function valueInBucket(value: number, bucket: DemographicBucketDef): boolean {
    const aboveMin = bucket.min === null || value >= bucket.min;
    const belowMax = bucket.max === null || value < bucket.max;
    return aboveMin && belowMax;
}

export function hasValidBucketLabels(buckets: DemographicBucketDef[]): boolean {
    return buckets.some((bucket) => bucket.label.trim().length > 0);
}

export function validateBuckets(buckets: DemographicBucketDef[]): string | undefined {
    if (!buckets.length) {
        return undefined;
    }

    const seenLabels = new Set<string>();
    for (const bucket of buckets) {
        const label = bucket.label.trim();
        if (!label) {
            return "Each range needs a label.";
        }
        if (seenLabels.has(label)) {
            return `Duplicate label "${label}".`;
        }
        seenLabels.add(label);

        if (bucket.min !== null && bucket.max !== null && bucket.min >= bucket.max) {
            return `Range "${label}" must have min less than max.`;
        }
    }

    return undefined;
}

function formatNumericBound(value: number | null): string {
    if (value === null) {
        return "∞";
    }
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatDateBound(value: number | null): string {
    if (value === null) {
        return "∞";
    }
    const date = new Date(value);
    return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;
}

function formatDatetimeBound(value: number | null): string {
    if (value === null) {
        return "∞";
    }
    const date = new Date(value);
    return `${formatDateBound(value)} ${padTwo(date.getHours())}:${padTwo(date.getMinutes())}`;
}

function formatTimeBound(value: number | null): string {
    if (value === null) {
        return "∞";
    }
    const hours = Math.floor(value / 60);
    const minutes = Math.floor(value % 60);
    return `${padTwo(hours)}:${padTwo(minutes)}`;
}

function formatBoundForKind(value: number | null, kind: DemographicBucketKind): string {
    switch (kind) {
        case "numeric":
            return formatNumericBound(value);
        case "date":
            return formatDateBound(value);
        case "datetime":
            return formatDatetimeBound(value);
        case "time":
            return formatTimeBound(value);
        default:
            return formatNumericBound(value);
    }
}

export function formatBucketRangeLabel(
    min: number | null,
    max: number | null,
    kind: DemographicBucketKind = "numeric",
): string {
    if (min === null && max === null) {
        return "All values";
    }
    if (min === null) {
        return `< ${formatBoundForKind(max, kind)}`;
    }
    if (max === null) {
        return `≥ ${formatBoundForKind(min, kind)}`;
    }
    return `${formatBoundForKind(min, kind)} – < ${formatBoundForKind(max, kind)}`;
}

export function comparableToInputValue(value: number | null, kind: DemographicBucketKind): string {
    if (value === null) {
        return "";
    }

    switch (kind) {
        case "numeric":
            return String(value);
        case "date":
            return formatDateBound(value);
        case "datetime": {
            const date = new Date(value);
            return `${formatDateBound(value)}T${padTwo(date.getHours())}:${padTwo(date.getMinutes())}`;
        }
        case "time":
            return formatTimeBound(value);
        default:
            return String(value);
    }
}

export function parseBucketInputValue(raw: string | number | null, kind: DemographicBucketKind): number | null {
    if (raw === null || raw === "") {
        return null;
    }

    if (kind === "numeric") {
        const parsed = typeof raw === "number" ? raw : Number.parseFloat(String(raw));
        return Number.isFinite(parsed) ? parsed : null;
    }

    const rawString = String(raw);
    if (kind === "time") {
        try {
            return timeToComparableMinutes(S25Util.date.parseTime(rawString));
        } catch {
            return null;
        }
    }

    if (kind === "date") {
        const startOfDay = S25Util.date.toStartOfDay(S25Util.date.parse(rawString));
        return startOfDay?.getTime() ?? null;
    }

    const parsedDatetime = tryParseDate(rawString);
    return parsedDatetime?.getTime() ?? null;
}

export function aggregateSlicesIntoBuckets(
    slices: DemographicSlice[],
    buckets: DemographicBucketDef[],
    kind: DemographicBucketKind,
): { name: string; total: number; unique: number }[] {
    const validBuckets = buckets.filter((bucket) => bucket.label.trim().length > 0);
    if (!validBuckets.length) {
        return slices.map((slice) => ({
            name: slice.value,
            total: slice.total,
            unique: slice.unique,
        }));
    }

    const bucketTotals = new Map<string, { total: number; unique: number }>();
    for (const bucket of validBuckets) {
        bucketTotals.set(bucket.label.trim(), { total: 0, unique: 0 });
    }

    let otherTotal = 0;
    let otherUnique = 0;
    let noneTotal = 0;
    let noneUnique = 0;

    for (const slice of slices) {
        const comparableValue = parseSliceComparableValue(slice.value, kind);
        if (comparableValue === null) {
            noneTotal += slice.total;
            noneUnique += slice.unique;
            continue;
        }

        const matchingBucket = validBuckets.find((bucket) => valueInBucket(comparableValue, bucket));
        if (!matchingBucket) {
            otherTotal += slice.total;
            otherUnique += slice.unique;
            continue;
        }

        const entry = bucketTotals.get(matchingBucket.label.trim())!;
        entry.total += slice.total;
        entry.unique += slice.unique;
    }

    const result: { name: string; total: number; unique: number }[] = validBuckets.map((bucket) => {
        const entry = bucketTotals.get(bucket.label.trim())!;
        return { name: bucket.label.trim(), total: entry.total, unique: entry.unique };
    });

    if (otherTotal > 0 || otherUnique > 0) {
        result.push({ name: OTHER_BUCKET_LABEL, total: otherTotal, unique: otherUnique });
    }
    if (noneTotal > 0 || noneUnique > 0) {
        result.push({ name: NONE_SLICE_VALUE, total: noneTotal, unique: noneUnique });
    }

    return result;
}

export function countComparableSlices(slices: DemographicSlice[], kind: DemographicBucketKind): number {
    return slices.filter((slice) => parseSliceComparableValue(slice.value, kind) !== null).length;
}

export function hasHighCardinality(slices: DemographicSlice[], kind: DemographicBucketKind): boolean {
    return countComparableSlices(slices, kind) > HIGH_CARDINALITY_SLICE_THRESHOLD;
}

export function suggestEqualBuckets(
    slices: DemographicSlice[],
    kind: DemographicBucketKind,
    count = DEFAULT_SUGGEST_BUCKET_COUNT,
): DemographicBucketDef[] {
    const comparableValues = slices
        .map((slice) => parseSliceComparableValue(slice.value, kind))
        .filter((value): value is number => value !== null);

    if (!comparableValues.length) {
        return [];
    }

    const dataMin = Math.min(...comparableValues);
    const dataMax = Math.max(...comparableValues);
    if (dataMin === dataMax) {
        return [
            {
                label: formatBucketRangeLabel(dataMin, null, kind),
                min: dataMin,
                max: null,
            },
        ];
    }

    const bucketCount = Math.max(1, Math.floor(count));
    const width = (dataMax - dataMin) / bucketCount;
    const suggested: DemographicBucketDef[] = [];

    for (let index = 0; index < bucketCount; index++) {
        const min = index === 0 ? dataMin : dataMin + width * index;
        const max = index === bucketCount - 1 ? null : dataMin + width * (index + 1);
        suggested.push({
            label: formatBucketRangeLabel(min, max, kind),
            min,
            max,
        });
    }

    return suggested;
}

export function emptyBucketDef(): DemographicBucketDef {
    return { label: "", min: null, max: null };
}

export function bucketRangeHint(kind: DemographicBucketKind): string {
    switch (kind) {
        case "date":
            return "Ranges are lower-inclusive and upper-exclusive by date (e.g. 2024-01-01 to 2024-07-01 means on or after Jan 1 and before Jul 1).";
        case "datetime":
            return "Ranges are lower-inclusive and upper-exclusive (e.g. start 2024-01-01 09:00, end 2024-07-01 09:00 means ≥ start and < end).";
        case "time":
            return "Ranges are lower-inclusive and upper-exclusive by time of day (e.g. 09:00 to 12:00 means ≥ 09:00 and < 12:00).";
        case "numeric":
        default:
            return "Ranges are lower-inclusive and upper-exclusive (e.g. 2.9-3.0 means ≥ 2.9 and < 3.0).";
    }
}
